import { AppUser, IAppUser } from "./app-user.model";
import { FileMedia, IFileMedia } from "./file-media.model";
import { HouseFlat, IHouseFlat } from "./house-flat.model";
import { IInputOutput, InputOutput } from "./input-output.model";
import { IProof, Proof } from "./proof.model";
import { IService, Service } from "./service.model";
import { IVisitType, VisitType } from "./visit-type.model";

export interface IVisit {
  visitId: string;
  visitTypeId: string;
  superVisitId?: string; // Usando ? para indicar que es opcional
  neighborhoodId: string;
  streetBuildingId?: string;
  houseFlatId?: string;
  contactId?: string; // Usando ? para indicar que es opcional
  names: string;
  paternalLastName: string;
  maternalLastName?: string; // Usando ? para indicar que es opcional
  plates: string;
  brand: string;
  model: string;
  color: string;
  description: string;
  isMotorcycle: boolean;
  status: boolean;
  approved?: boolean;
  passengersNumber: number;
  dueDate?: string
  manuallyClosed: boolean;
  withOCR: boolean;
  withAI: boolean;
  token: string;
  arrivedAt?: string; // Usando ? para indicar que es opcional
  departedAt?: string; // Usando ? para indicar que es opcional
  uidCreatedBy?: string;
  uidUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
  visitType?: IVisitType;
  houseFlat?: IHouseFlat;
  housesFlats?: IHouseFlat[]; // Solo viene con 2 o más casas (servicio); con una sola casa viene vacío y la casa va en houseFlat
  service?: IService;
  proofs?: IProof[];
  inputsOutputs?: IInputOutput[];
  createdBy?: IAppUser;
}

export class Visit {
  visitId: string;
  visitTypeId: string;
  superVisitId?: string; // Usando ? para indicar que es opcional
  neighborhoodId: string;
  streetBuildingId?: string;
  houseFlatId?: string;
  contactId?: string; // Usando ? para indicar que es opcional
  names: string;
  paternalLastName: string;
  maternalLastName?: string; // Usando ? para indicar que es opcional
  plates: string;
  brand: string;
  model: string;
  color: string;
  description: string;
  isMotorcycle: boolean;
  status: boolean;
  approved?: boolean;
  passengersNumber: number;
  dueDate?: string
  manuallyClosed: boolean;
  withOCR: boolean;
  withAI: boolean;
  token: string;
  arrivedAt?: Date; // Usando ? para indicar que es opcional
  departedAt?: Date; // Usando ? para indicar que es opcional
  uidCreatedBy?: string;
  uidUpdatedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  visitType?: VisitType;
  houseFlat?: HouseFlat;
  housesFlats: HouseFlat[];
  proofs: Proof[];
  inputsOutputs: InputOutput[];
  service?: Service;
  createdBy?: AppUser;

  constructor(data: IVisit) {
    this.visitId = data.visitId;
    this.visitTypeId = data.visitTypeId;
    this.superVisitId = data.superVisitId;
    this.neighborhoodId = data.neighborhoodId;
    this.streetBuildingId = data.streetBuildingId;
    this.houseFlatId = data.houseFlatId;
    this.contactId = data.contactId;
    this.names = data.names;
    this.paternalLastName = data.paternalLastName;
    this.maternalLastName = data.maternalLastName;
    this.plates = data.plates;
    this.brand = data.brand;
    this.model = data.model;
    this.color = data.color;
    this.description = data.description;
    this.isMotorcycle = data.isMotorcycle;
    this.status = data.status;
    this.approved = data.approved;
    this.passengersNumber = data.passengersNumber;
    this.dueDate = data.dueDate;
    this.manuallyClosed = data.manuallyClosed;
    this.withOCR = data.withOCR;
    this.withAI = data.withAI;
    this.token = data.token;
    this.arrivedAt = data.arrivedAt ? new Date(data.arrivedAt) : undefined;
    this.departedAt = data.departedAt ? new Date(data.departedAt) : undefined;
    this.uidCreatedBy = data.uidCreatedBy;
    this.uidUpdatedBy = data.uidUpdatedBy;
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.visitType = data.visitType 
      ? new VisitType(data.visitType) 
      : undefined
    this.houseFlat = data.houseFlat
      ? new HouseFlat(data.houseFlat)
      : undefined;
    this.housesFlats = data.housesFlats
      ? data.housesFlats.map(houseFlat => new HouseFlat(houseFlat))
      : [];
    this.proofs = data.proofs
      ? data.proofs.map(proof => new Proof(proof))
      : [];
    this.service = data.service
      ? new Service(data.service)
      : undefined;
    this.createdBy = data.createdBy
      ? new AppUser(data.createdBy)
      : undefined;
    this.inputsOutputs = data.inputsOutputs
      ? data.inputsOutputs.map(inputOutput => new InputOutput(inputOutput))
      : [];
  }

  get fullName(): string {

    return `${this.names} ${this.paternalLastName} ${this.maternalLastName}`;
  }

  /**
   * Casas de la visita cuando son 2 o más, con la principal (houseFlat) primero.
   * El orden de housesFlats no es confiable, por eso la principal siempre sale
   * de houseFlat. Con menos de 2 casas regresa [] y la visita se pinta con
   * houseFlat, igual que siempre.
   */
  get multipleHousesFlats(): HouseFlat[] {
    if (this.housesFlats.length < 2) {
      return [];
    }

    const principal = this.houseFlat
      ?? this.housesFlats.find(houseFlat => houseFlat.houseFlatId === this.houseFlatId);

    if (!principal) {
      return this.housesFlats;
    }

    return [
      principal,
      ...this.housesFlats.filter(houseFlat => houseFlat.houseFlatId !== principal.houseFlatId)
    ];
  }

  /**
   * Nombre de la calle de una casa solo si es distinta a la de la principal;
   * si es la misma regresa '' para no repetirla en cada etiqueta.
   */
  otherStreetName(houseFlat: HouseFlat): string {
    const streetName = houseFlat.streetBuilding?.name ?? '';
    const principalStreetName = this.multipleHousesFlats[0]?.streetBuilding?.name ?? '';

    return streetName !== principalStreetName ? streetName : '';
  }

  get isVehicle(): boolean {
    return this.visitType?.description === 'En vehículo' || this.visitType?.description === 'Servicio' || this.visitType?.description === 'Proveedor';
  }
}
